/**
 * 验证邮箱落地页（OPT-051）
 *
 * 公开路由 /verify-email?token=xxx，进页自动 POST /api/auth/verify-email。
 * GET 预拉（邮件客户端/安全网关）只渲染页面、不消费 token——消费只发生在用户真实打开后的 POST。
 * token 单次使用即清空：重复点击时端点返回 400，若已登录且 me.emailVerified=true 则显示"已验证"态（design D3）。
 */
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth'

type PageState = 'verifying' | 'success' | 'already' | 'fail' | 'missing'

export function VerifyEmailPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const qc = useQueryClient()
  const [state, setState] = useState<PageState>('verifying')

  useEffect(() => {
    const token = searchParams.get('token')
    if (!token) {
      setState('missing')
      return
    }
    let cancelled = false
    api.verifyEmail(token)
      .then(() => {
        if (cancelled) return
        setState('success')
        // 让 Layout 横幅立即消失
        qc.invalidateQueries({ queryKey: ['me'] })
      })
      .catch(async () => {
        if (cancelled) return
        // token 单次使用：已验证用户重复点击会 400——若已登录且确实已验证，显示"已验证"而非错误
        if (isAuthenticated()) {
          try {
            const me = await api.me()
            if (!cancelled && me.emailVerified) {
              setState('already')
              return
            }
          } catch { /* me 失败按失败态处理 */ }
        }
        if (!cancelled) setState('fail')
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const authed = isAuthenticated()

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md bg-white rounded-lg border border-slate-200 p-8 text-center">
        {state === 'verifying' && (
          <p className="text-slate-600">{t('verifyEmail.verifying')}</p>
        )}

        {(state === 'success' || state === 'already') && (
          <>
            <h1 className="text-xl font-bold text-slate-900 mb-2">
              {state === 'already' ? t('verifyEmail.alreadyVerified') : t('verifyEmail.successTitle')}
            </h1>
            <p className="text-slate-600 mb-6">{t('verifyEmail.successDesc')}</p>
            <Button asChild>
              <Link to={authed ? '/app' : '/'}>{authed ? t('verifyEmail.goApp') : t('verifyEmail.goLogin')}</Link>
            </Button>
          </>
        )}

        {(state === 'fail' || state === 'missing') && (
          <>
            <h1 className="text-xl font-bold text-slate-900 mb-2">{t('verifyEmail.failTitle')}</h1>
            <p className="text-slate-600 mb-2">
              {state === 'missing' ? t('verifyEmail.missingToken') : t('verifyEmail.failDesc')}
            </p>
            <p className="text-sm text-slate-500 mb-6">{t('verifyEmail.resendHint')}</p>
            <Button asChild variant="outline">
              <Link to={authed ? '/app' : '/'}>{authed ? t('verifyEmail.goApp') : t('verifyEmail.goLogin')}</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
